import { useParams, useNavigate } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { IconArrow, IconMapPin, IconWhatsapp } from '../components/Icons';
import styles from './PackageDetails.module.css';

export default function PackageDetails() {
  const { slug } = useParams();
  const { data } = useSite();
  const navigate = useNavigate();

  const pkg = (data.corporate?.packages || []).find(p => p.slug === slug);

  if (!pkg) {
    return (
      <>
        <SEO
          title="Package Not Found | Keepers Corporate"
          description="The corporate package you're looking for is not available. Explore other team-building packages."
          noindex={true}
        />
        <div className={styles.page}>
          <Navbar />
          <div className={styles.notFound}>
            <div className={styles.notFoundIcon}>📦</div>
            <h2>Package not found</h2>
            <button className={styles.backBtn} onClick={() => navigate('/corporate')}>
              <span className={styles.backArrow}><IconArrow size={14} /></span> Back to Corporate
            </button>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const bookOnWhatsApp = () => {
    const phone = (data.contact?.whatsapp || '').replace(/[^0-9]/g, '');
    const msg = encodeURIComponent(
      `Hi! I'm interested in the following corporate package:

📦 Package: ${pkg.title}
📍 Destination: ${pkg.destination}
👥 Employees: ${pkg.employees}
💰 Price: ${pkg.price}

📋 Details:
${pkg.details}

Please get in touch!`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  return (
    <>
      <SEO
        title={`${pkg.title} - Corporate Package | Keepers Experience`}
        description={pkg.details ? pkg.details.substring(0, 160) : `Join us for ${pkg.title} in ${pkg.destination}`}
        image={pkg.image || 'https://keepersexperience.com/full_logo.png'}
        url={`https://keepersexperience.com/corporate/packages/${pkg.slug}`}
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": pkg.title,
          "description": pkg.details,
          "image": pkg.image,
          "offers": {
            "@type": "Offer",
            "priceCurrency": "JOD",
            "price": pkg.price
          }
        }}
      />
      <div className={styles.page}>
      <Navbar />

      {/* ─── BACK BUTTON ─── */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <span className={styles.backArrow}><IconArrow size={14} /></span>
          Back
        </button>
      </div>

      {/* ─── HERO IMAGE ─── */}
      <div className={styles.heroSection}>
        {pkg.image ? (
          <>
            <img src={pkg.image} alt={pkg.title} className={styles.heroImg} />
            <div className={styles.heroOverlay} />
          </>
        ) : (
          <div className={styles.heroPlaceholder}>📦</div>
        )}

        <span className={styles.heroBadge}>Corporate</span>

        {/* Title overlay at bottom of hero */}
        <div className={styles.heroBottom}>
          <div className={styles.heroPriceBadge}>{pkg.price}</div>
          <h1 className={styles.heroTitle}>{pkg.title}</h1>
        </div>
      </div>

      {/* ─── CONTENT ─── */}
      <div className={styles.content}>
        <div className={styles.contentInner}>

          {/* Info Grid */}
          <div className={styles.infoGrid}>
            {pkg.destination && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}><IconMapPin size={18} /></span>
                <div>
                  <div className={styles.infoLabel}>Destination</div>
                  <div className={styles.infoValue}>{pkg.destination}</div>
                </div>
              </div>
            )}
            {pkg.employees && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}>👥</span>
                <div>
                  <div className={styles.infoLabel}>Team Size</div>
                  <div className={styles.infoValue}>{pkg.employees} employees</div>
                </div>
              </div>
            )}
            {pkg.price && (
              <div className={styles.infoCard}>
                <span className={styles.infoIcon}>💰</span>
                <div>
                  <div className={styles.infoLabel}>Price</div>
                  <div className={styles.infoValue}>{pkg.price}</div>
                </div>
              </div>
            )}
          </div>

          {/* Details */}
          {pkg.details && (
            <div className={styles.descSection}>
              <h2 className={styles.descTitle}>About this package</h2>
              <p className={styles.desc}>{pkg.details}</p>
            </div>
          )}

          {/* CTA */}
          <div className={styles.cta}>
            <button className={styles.whatsappBtn} onClick={bookOnWhatsApp}>
              <IconWhatsapp size={18} /> Book via WhatsApp
            </button>
            <button className={styles.allBtn} onClick={() => navigate('/corporate')}>
              <span className={styles.allBtnArrow}><IconArrow size={14} /></span>
              All Packages
            </button>
          </div>

        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}
